/**
 * Etiqueta de alimento em PDF — 60x40 mm, impressora termica direta.
 *
 * POR QUE NAO SEGUE O PADRAO `html` + `blocos` DE recibo.js. Aquele padrao
 * existe porque comanda e FLUXO DE TEXTO: o vocabulario de blocos descreve bem
 * "titulo, linha, divisoria, total". Etiqueta e LAYOUT POSICIONAL — o QR fica
 * num canto fixo, a validade em corpo maior na quarta linha, tudo disputando
 * 54x34 mm uteis. Manter duas versoes pixel-a-pixel disso (uma em HTML para a
 * web, outra em blocos para o app) seria criar dois lugares para o mesmo bug.
 * Aqui o PDF e o unico caminho, nos dois lados.
 *
 * Isso NAO contraria a licao do recibo.js: aquela e contra o html2canvas, que
 * rasteriza a tela e saiu em branco no WKWebView. Continuamos desenhando com
 * primitivas do jsPDF — so que agora com coordenadas em vez de fluxo.
 *
 * A VALIDADE E O ELEMENTO MAIS IMPORTANTE DA ETIQUETA. Quem abre a geladeira
 * as sete da manha, de oculos errado, precisa ler a data a um metro. Por isso
 * ela e a unica coisa em corpo grande, e por isso o QR nunca e o unico
 * caminho: papel termico desbota com umidade e gordura.
 */

import { matrizDeQr } from './qr';
import { entregarArquivo } from './arquivos';

export const LARGURA_MM = 60;
export const ALTURA_MM = 40;

const MARGEM = 3;
const QR_LADO = 14;
// Duas linhas: e o que sobra entre a validade e a borda de baixo (ver
// linhasDoRodape). Mexer aqui sem refazer a conta escreve fora do papel.
const LINHAS_RODAPE = 2;

// Impressora termica comum e 203 dpi = 8 dots/mm. Um modulo de QR precisa cair
// num numero INTEIRO de dots: se cair em 4,4 dots, o driver arredonda uns para
// 4 e outros para 5, os modulos ficam de larguras diferentes e o leitor erra em
// angulo ou com pouca luz. E a causa classica de "o QR nao le".
const DOTS_POR_MM = 8;

const ROTULO_CONSERVACAO = {
  ambiente: 'TEMP. AMBIENTE',
  refrigerado: 'REFRIGERADO',
  congelado: 'CONGELADO',
  descongelado: 'DESCONGELADO',
};

/**
 * Desenha o QR com cada modulo alinhado ao grid de dots da impressora.
 * Devolve o lado real usado (pode ser menor que o pedido, pelo arredondamento).
 */
function desenharQr(doc, texto, x, y, ladoDesejado) {
  const matriz = matrizDeQr(texto);
  const modulos = matriz.length;

  // Maior modulo que cabe, em dots inteiros. Piso de 1 dot para nunca sumir.
  const dotsPorModulo = Math.max(1, Math.floor((ladoDesejado * DOTS_POR_MM) / modulos));
  const modulo = dotsPorModulo / DOTS_POR_MM;
  const lado = modulo * modulos;

  // A sobra do arredondamento vira centralizacao, nao distorcao.
  const ox = x + (ladoDesejado - lado) / 2;
  const oy = y + (ladoDesejado - lado) / 2;

  doc.setFillColor(0, 0, 0);
  for (let l = 0; l < modulos; l += 1) {
    for (let c = 0; c < modulos; c += 1) {
      if (matriz[l][c]) {
        doc.rect(ox + c * modulo, oy + l * modulo, modulo, modulo, 'F');
      }
    }
  }
  return lado;
}

/**
 * O que cabe no rodape, em ordem de importancia.
 *
 * Sao 60x40mm com um QR de 13mm no canto: o rodape tem espaco para DUAS linhas,
 * e os candidatos sao tres. A ordem nao e arbitraria — lote e o que rastreia,
 * responsavel e o que a boa pratica pede, e a validade do fabricante e a menos
 * acionavel das tres (a que vale para quem abre a geladeira ja esta impressa
 * em corpo grande logo acima).
 *
 * Sem esse corte, uma etiqueta de insumo aberto com os tres campos escrevia
 * fora da pagina — o texto simplesmente sumia na impressao, sem erro nenhum.
 */
export function linhasDoRodape(e, maxLinhas = LINHAS_RODAPE) {
  const candidatos = [
    e.lote && `LOTE: ${e.lote}`,
    e.responsavel && `RESP.: ${e.responsavel}`,
    e.validadeOriginal && `VAL. ORIG.: ${e.validadeOriginal}`,
  ].filter(Boolean);
  return candidatos.slice(0, maxLinhas);
}

/** Corta o texto na largura dada, devolvendo no maximo `maxLinhas` linhas. */
function linhasQueCabem(doc, texto, largura, maxLinhas) {
  const linhas = doc.splitTextToSize(String(texto || ''), largura);
  if (linhas.length <= maxLinhas) return linhas;
  const cortadas = linhas.slice(0, maxLinhas);
  cortadas[maxLinhas - 1] = `${String(cortadas[maxLinhas - 1]).slice(0, -1)}…`;
  return cortadas;
}

/**
 * O que sai no canto direito: quanto tem NESTE pote.
 *
 * Sem a contagem de copias ("x8"). Cada papel impresso e independente: sai da
 * impressora, e colado num pote e segue a vida sozinho. Quantos potes iguais
 * existem, e quantos ainda restam, e controle que vive na tela — o papel nao
 * tem como se atualizar quando o nono pote acabar, entao imprimir o total so
 * confunde quem le o pote na mao.
 */
export function textoDaQuantidade(e) {
  if (!e || e.quantidade == null || e.quantidade === '') return '';
  return `${e.quantidade}${e.unidade ? ` ${e.unidade}` : ''}`;
}

/** Espacamento entre as linhas do rodape, em mm. */
const PASSO_RODAPE = 3.2;

/**
 * Onde cada linha do rodape escreve, contando de baixo para cima.
 *
 * A ultima linha fica sempre no mesmo lugar — encostada no pe da etiqueta —
 * e as anteriores sobem a partir dela. E o que garante que o rodape nunca
 * dependa de quantas linhas o nome ocupou.
 */
export function basesDoRodape(quantidade) {
  const ultima = ALTURA_MM - MARGEM - 0.6;
  return Array.from({ length: Math.max(0, quantidade) },
    (_, i) => ultima - (quantidade - 1 - i) * PASSO_RODAPE);
}

/**
 * Desenha uma etiqueta na pagina corrente.
 *
 * @param {object} e
 *   { nome, conservacao, quantidade, unidade, manipulado, validade, lote,
 *     responsavel, codigo, url, validadeOriginal }
 */
function desenharEtiqueta(doc, e) {
  const util = LARGURA_MM - MARGEM * 2;
  const colunaTexto = LARGURA_MM - MARGEM * 2 - QR_LADO - 2;
  let y = MARGEM + 3.2;

  // Nome — ate 2 linhas, o que sobra vira reticencia.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(0);
  linhasQueCabem(doc, (e.nome || '').toUpperCase(), util, 2).forEach((linha) => {
    doc.text(linha, MARGEM, y);
    y += 3.9;
  });

  // Conservacao a esquerda, quantidade a direita.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(70);
  doc.text(ROTULO_CONSERVACAO[e.conservacao] || String(e.conservacao || '').toUpperCase(),
    MARGEM, y);
  if (e.quantidade) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text(textoDaQuantidade(e), LARGURA_MM - MARGEM, y, { align: 'right' });
  }
  y += 2;

  doc.setDrawColor(150);
  doc.setLineWidth(0.2);
  doc.line(MARGEM, y, LARGURA_MM - MARGEM, y);
  y += 3.6;

  // Manipulacao.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(70);
  doc.text('MANIP.', MARGEM, y);
  doc.setTextColor(0);
  doc.text(String(e.manipulado || ''), MARGEM + 12.5, y);
  y += 4.5;

  // Validade — o elemento que precisa ser lido a um metro.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(70);
  doc.text('VALIDADE', MARGEM, y - 0.4);
  doc.setTextColor(0);
  // A data e o unico elemento que precisa ser lido de longe, com a mao ocupada.
  // Com o ano ela ficou mais larga, e num prazo curto ela leva a hora junto —
  // por isso o corpo cede antes de encostar no QR, em vez de escrever por baixo
  // dele. `getTextWidth` respeita a fonte corrente, entao a medida e a real.
  const textoValidade = String(e.validade || '');
  const larguraMaxima = LARGURA_MM - MARGEM - QR_LADO - MARGEM - 1.5;
  let corpoValidade = 13.5;
  doc.setFontSize(corpoValidade);
  while (corpoValidade > 8 && doc.getTextWidth(textoValidade) > larguraMaxima) {
    corpoValidade -= 0.5;
    doc.setFontSize(corpoValidade);
  }
  doc.text(textoValidade, MARGEM, y + 4.9);

  // Rodape: lote e responsavel a esquerda, QR a direita.
  //
  // ANCORADO NO PE, e nao na posicao corrente. Antes ele herdava o `y` que
  // vinha descendo desde o nome, entao um nome de duas linhas empurrava tudo
  // para baixo — foi assim que o texto ja foi parar em y=39,1 numa pagina de
  // 40 mm. Ancorado, ele nao pode ser empurrado para fora, e as fontes acima
  // podem crescer sem reabrir esse risco.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(70);
  const textosRodape = linhasDoRodape(e);
  const bases = basesDoRodape(textosRodape.length);
  textosRodape.forEach((texto, i) => {
    linhasQueCabem(doc, texto, colunaTexto, 1)
      .forEach((linha) => { doc.text(linha, MARGEM, bases[i]); });
  });

  const qrX = LARGURA_MM - MARGEM - QR_LADO;
  const qrY = ALTURA_MM - MARGEM - QR_LADO - 2.6;
  if (e.url || e.codigo) {
    desenharQr(doc, e.url || e.codigo, qrX, qrY, QR_LADO);
  }
  if (e.codigo) {
    doc.setFontSize(6.5);
    doc.setTextColor(90);
    doc.text(String(e.codigo), qrX + QR_LADO / 2, ALTURA_MM - MARGEM + 0.4, { align: 'center' });
  }
}

/**
 * Monta o PDF com uma etiqueta por pagina.
 *
 * Varias etiquetas num PDF so = UM job de impressao. Doze PDFs separados
 * significariam doze dialogos de impressao, e a fornada de brigadeiro nao sai.
 *
 * @param {Array<object>} etiquetas
 * @returns {Promise<object>} documento jsPDF
 */
export async function pdfDeEtiquetas(etiquetas) {
  const lista = (etiquetas || []).filter(Boolean);
  if (!lista.length) throw new Error('nenhuma etiqueta para imprimir');

  const { jsPDF } = await import('jspdf');
  // `orientation: 'landscape'` NAO e enfeite: o jsPDF normaliza o formato para
  // a orientacao, e em retrato (o padrao) ele TROCA a ordem para que a altura
  // seja a maior — [60,40] virava uma pagina de 40x60. A etiqueta sairia
  // girada, cortada pela impressora, e so na impressora.
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [LARGURA_MM, ALTURA_MM] });

  lista.forEach((etiqueta, i) => {
    if (i > 0) doc.addPage([LARGURA_MM, ALTURA_MM], 'landscape');
    desenharEtiqueta(doc, etiqueta);
  });
  return doc;
}

/**
 * Etiqueta de calibracao: moldura a 1 mm da borda.
 *
 * Se a moldura sai inteira e centrada, o driver esta com o papel certo. Se sai
 * cortada ou minuscula no meio de uma folha, esta em A4 com "ajustar a pagina".
 * E o teste que separa "instalei a impressora" de um chamado de suporte por
 * semana — e custa dez linhas.
 */
export async function pdfDeTeste() {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [LARGURA_MM, ALTURA_MM] });

  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(1, 1, LARGURA_MM - 2, ALTURA_MM - 2);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('TESTE DE IMPRESSAO', LARGURA_MM / 2, 12, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(`${LARGURA_MM} x ${ALTURA_MM} mm`, LARGURA_MM / 2, 18, { align: 'center' });
  doc.text('A moldura deve aparecer', LARGURA_MM / 2, 24, { align: 'center' });
  doc.text('inteira e centralizada.', LARGURA_MM / 2, 28, { align: 'center' });
  return doc;
}

/**
 * Entrega o PDF para impressao.
 *
 * Na web abre numa aba, que e onde o dialogo de impressao acontece; se o
 * bloqueador de pop-up barrar (acontece fora de gesto de clique), cai para
 * download, que o `arquivos.js` ja sabe fazer nos dois mundos.
 */
export async function imprimirEtiquetas(etiquetas, nome = 'etiquetas.pdf') {
  const doc = await pdfDeEtiquetas(etiquetas);
  const blob = doc.output('blob');

  if (typeof window !== 'undefined' && window.open) {
    const url = URL.createObjectURL(blob);
    const aba = window.open(url, '_blank');
    if (aba) {
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return true;
    }
    URL.revokeObjectURL(url);
  }
  return entregarArquivo(blob, nome);
}
